import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import AppBar from './components/AppBar';
import BottomTabBar from './components/BottomTabBar';
import WelcomeSheet from './components/WelcomeSheet';
import CompletionSheet from './components/CompletionSheet';
import AbandonSheet from './components/AbandonSheet';
import StartFailedSheet from './components/StartFailedSheet';
import SyncBanner from './components/SyncBanner';
import MyCrawl from './screens/MyCrawl/MyCrawl';
import EmptyState from './screens/EmptyState/EmptyState';
import LoadingWidget from './components/LoadingWidget';
import FAQs from './pages/FAQs';
import Crawls from './screens/Crawls/Crawls';
import Leaderboard from './screens/Leaderboard/Leaderboard';
import { SessionService } from './services/sessionService';
import { getQueryParam } from './utils/urlUtils';
import { useSessionOrchestration } from './hooks/useSessionOrchestration';
import './App.css';

function App() {
  const {
    isLoading,
    currentSession,
    setCurrentSession,
    refreshTrigger,
    showWelcomePopup,
    showCompletionPopup,
    completedSession,
    setCompletedSession,
    showAbandonPopup,
    abandonPopupData,
    handleSessionCreated,
    handleCompletionPopupClose,
    handleAbandonCrawl,
    handleCancelAbandon,
    showStartFailed,
    handleRetryStart,
    handleCloseStartFailed,
    pendingPubIds,
    syncNotice,
    handleRetrySync,
    handleDismissSyncNotice,
  } = useSessionOrchestration();

  const handleCompletionNameChange = async (newName: string) => {
    if (!completedSession) return;
    try {
      const updatedSession = await SessionService.updateSession(completedSession.id, {
        contenderName: newName,
      });
      setCompletedSession(updatedSession);
      setCurrentSession(updatedSession);
    } catch (e) {
      console.error('Failed to update player name', e);
      throw e;
    }
  };

  if (isLoading) {
    return <LoadingWidget message="Loading session data..." />;
  }

  return (
    <Router>
      <div className="App">
        <AppBar session={currentSession} />

        <main className={syncNotice ? 'main-content main-content-banner' : 'main-content'}>
          <Routes>
            <Route path="/" element={(SessionService.getSessionId() || getQueryParam('pubId')) ? <MyCrawl key={refreshTrigger} session={currentSession} pendingPubIds={pendingPubIds} /> : <EmptyState />} />
            <Route path="/crawls" element={<Crawls />} />
            <Route path="/crawls/:slug" element={<Leaderboard />} />
            <Route path="/faqs" element={<FAQs />} />
          </Routes>
        </main>

        <BottomTabBar session={currentSession} />

        <SyncBanner
          notice={syncNotice}
          onRetry={handleRetrySync}
          onDismiss={handleDismissSyncNotice}
        />

        <StartFailedSheet
          isOpen={showStartFailed}
          onRetry={handleRetryStart}
          onClose={handleCloseStartFailed}
        />

        <WelcomeSheet isOpen={showWelcomePopup} onSessionCreated={handleSessionCreated} />

        <CompletionSheet
          isOpen={showCompletionPopup}
          session={completedSession}
          onClose={handleCompletionPopupClose}
          onNameChange={handleCompletionNameChange}
        />

        <AbandonSheet
          isOpen={showAbandonPopup}
          data={abandonPopupData}
          session={currentSession}
          onAbandon={handleAbandonCrawl}
          onCancel={handleCancelAbandon}
        />
      </div>
    </Router>
  );
}

export default App;
